import React, { memo } from 'react';
import styled from 'styled-components';
import upperFirst from 'lodash/upperFirst';

import colors from '@front/common/src/utils/colors';
import { getCurrentCountry } from '@front/common/src/utils/country';

import { SinglePrestationsPricesCleaningProps } from '@front/customer/src/components/basket/types';
import { getFormattedPrice } from '@front/customer/src/utils/cleaning';
import { CONFIG_BY_COUNTRY } from '@front/customer/src/constants/country-config';

import { translate } from '@front/i18n';

import Accordion from '@front/web-ui/src/components/accordion/accordion';
import Separator from '@front/web-ui/src/components/separator/separator';
import Text from '@front/web-ui/src/components/base-text/variants/text';
import Skeleton from '@front/web-ui/src/components/skeleton/skeleton';

import PromoCode from '../../../promo-code/promo-code';
import useBasketSection from '../../../../hooks/use-basket-section';
import { route } from '../../../../steps/steps';
import Category from '../common/category';
import Surcharges from '../common/surcharges';
import AccordionSubtitle from '../common/accordion-subtitle';

import SinglePricesWithTax from './single-prices-with-tax';

const t = translate('basket.cleaning.services');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 20px;
`;

const StyledAccordion = styled(Accordion)`
  margin-top: 20px;
`;

const SinglePrestationsPrices = ({
  cleaningPrestation,
  cleaningPrestationOptions,
  duration,
  hasPets,
  isLoadingPrices,
  mode,
  prices,
  promotion,
  universeReference,
  user,
  shouldShowPromoCode,
}: SinglePrestationsPricesCleaningProps) => {
  const { hasTaxCreditInBasket } = CONFIG_BY_COUNTRY[getCurrentCountry()];

  const hasPrestations = !!cleaningPrestation;
  const hasDuration = !!duration;
  const hasTaxCredit = prices && prices.tax_credit.price > 0;
  const formattedPriceAfterTax = getFormattedPrice(hasDuration, prices?.after_tax);

  const { isOpen, isVisible } = useBasketSection({
    data: hasPrestations,
    sectionLocations: [
      route('prestations'),
      route('frequency'),
      route('duration'),
      route('options'),
      route('animals'),
    ],
  });

  if (!isVisible) return null;

  return (
    <Container>
      <Separator />
      <StyledAccordion
        icon='basket'
        iconLabel={t('basket.services.icon_label.basket')}
        title={upperFirst(t('universes.cleaning.title'))}
        titlePreview={isLoadingPrices ? <Skeleton linesWidth={['110px']} /> : formattedPriceAfterTax}
        subtitle={
          hasTaxCreditInBasket ? (
            <AccordionSubtitle
              hasTaxCredit={hasTaxCredit}
              isLoading={isLoadingPrices}
              formattedPrice={getFormattedPrice(hasDuration, prices?.total)}
            />
          ) : undefined
        }
        subtitleVisibility='close'
        isOpen={isOpen}
      >
        {hasPrestations ? (
          <>
            <Category
              cleaningPrestation={cleaningPrestation}
              cleaningPrestationOptions={cleaningPrestationOptions}
              duration={duration}
              hasPets={hasPets}
              frequencyText={t('.frequency.spot')}
            />
            <Surcharges surcharges={prices?.surcharges} />
            <SinglePricesWithTax
              hasDuration={hasDuration}
              hasPrestations={hasPrestations}
              isLoading={isLoadingPrices}
              prices={prices}
            />
          </>
        ) : (
          <Text.SM color={colors.ink50}>{t('.no_services')}</Text.SM>
        )}
      </StyledAccordion>
      {mode === 'creation' && hasPrestations && shouldShowPromoCode && (
        <PromoCode
          cleaningPrestation={cleaningPrestation}
          promotion={promotion}
          totalPrice={prices?.total.price || 0}
          universeReference={universeReference}
          user={user}
        />
      )}
    </Container>
  );
};

export default memo(SinglePrestationsPrices);
